/**
 * Data Cache Context
 *
 * Thin pass-through to backend Redis cache with real-time invalidation.
 *
 * Architecture:
 * - Redis is the PRIMARY shared cache across ALL admin users
 * - Frontend does NOT maintain its own cache or TTL
 * - Cache updates ONLY when: (1) Data changes via mutation, (2) TTL expires in Redis
 * - All API endpoints return cached data from Redis (1 hour TTL per ADR-080)
 * - WebSocket receives real-time cache invalidation events
 * - Components refetch only affected data (no full page reload)
 *
 * This context provides:
 * - Unified API for data fetching
 * - Request deduplication (prevent duplicate in-flight requests)
 * - Preloading on login for instant page loads
 * - Real-time cache invalidation via WebSocket
 * - Granular data refresh (components update individually)
 *
 * Related ADRs:
 * - ADR-080: Admin Portal Redis Caching Layer
 * - SPEC-ADMIN-001: Cloud-First Architecture
 */

import React, { createContext, useContext, useCallback, useRef, useEffect, useState } from 'react';
import { usersApi, employeeApi, organizationsApi, alertsApi, feedbackApi, financeApi, economicsApi, analyticsApi, supportApi, tiersApi, monitoringApi, healthApi, kbApi } from '../services/adminApi';
import { useCacheEvents } from '../hooks/useCacheEvents';

const DataCacheContext = createContext({});

export const useDataCache = () => {
  const context = useContext(DataCacheContext);
  if (!context) {
    throw new Error('useDataCache must be used within a DataCacheProvider');
  }
  return context;
};

// Frontend cache TTL (30 seconds) - avoids redundant network calls during rapid navigation
const CACHE_TTL_MS = 30 * 1000;

export const DataCacheProvider = ({ children }) => {
  // In-flight requests to prevent duplicate fetches
  const pendingRequests = useRef({});

  // Short-lived frontend response cache (eliminates network calls for recent data)
  // Format: { key: { data, timestamp } }
  const responseCache = useRef({});

  // Track if preload has already been called this session
  const hasPreloaded = useRef(false);

  // WebSocket cache events integration
  const { isConnected, lastEvent, subscribe } = useCacheEvents();

  // Track invalidation callbacks for components to subscribe
  const invalidationCallbacks = useRef(new Map());

  // State to trigger refetches (components can watch this)
  const [invalidationCounter, setInvalidationCounter] = useState({
    users: 0,
    employees: 0,
    organizations: 0,
    alerts: 0,
    feedback: 0,
    analytics: 0,
    economics: 0,
    tiers: 0,
    monitoring: 0,
    kb: 0,
  });

  /**
   * Get cached value SYNCHRONOUSLY (no Promise)
   * Use this to initialize component state with cached data for instant render.
   *
   * @param key Cache key
   * @returns Cached data or null if not in cache/expired
   */
  const getCachedValue = useCallback((key) => {
    const cached = responseCache.current[key];
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      return cached.data;
    }
    return null;
  }, []);

  /**
   * Fetch with deduplication and short-lived frontend cache
   * 1. Check frontend cache (30s TTL) - instant for recent data
   * 2. Return in-flight promise if duplicate request
   * 3. Otherwise fetch from backend (Redis cached, 1hr TTL)
   */
  const fetchWithDedup = useCallback(async (key, fetcher) => {
    // Check frontend cache first (eliminates network call for recent data)
    const cached = responseCache.current[key];
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      return cached.data;
    }

    // Return existing promise if request is in-flight
    if (pendingRequests.current[key]) {
      return pendingRequests.current[key];
    }

    // Make new request
    const promise = fetcher()
      .then(data => {
        delete pendingRequests.current[key];
        // Cache the response for 30 seconds
        responseCache.current[key] = { data, timestamp: Date.now() };
        return data;
      })
      .catch(err => {
        delete pendingRequests.current[key];
        throw err;
      });

    pendingRequests.current[key] = promise;
    return promise;
  }, []);

  // =========================================================================
  // Invalidation helpers for mutations (defined early for useEffect)
  // These clear pending requests - actual cache invalidation
  // happens in backend Redis when mutations occur
  // =========================================================================

  const invalidateUsers = useCallback(() => {
    Object.keys(pendingRequests.current)
      .filter(k => k.startsWith('users_'))
      .forEach(k => delete pendingRequests.current[k]);
    Object.keys(responseCache.current)
      .filter(k => k.startsWith('users_'))
      .forEach(k => delete responseCache.current[k]);
  }, []);

  const invalidateEmployees = useCallback(() => {
    Object.keys(pendingRequests.current)
      .filter(k => k.startsWith('employees'))
      .forEach(k => delete pendingRequests.current[k]);
    Object.keys(responseCache.current)
      .filter(k => k.startsWith('employees'))
      .forEach(k => delete responseCache.current[k]);
  }, []);

  const invalidateOrganizations = useCallback(() => {
    Object.keys(pendingRequests.current)
      .filter(k => k.startsWith('orgs_'))
      .forEach(k => delete pendingRequests.current[k]);
    Object.keys(responseCache.current)
      .filter(k => k.startsWith('orgs_'))
      .forEach(k => delete responseCache.current[k]);
  }, []);

  const invalidateAlerts = useCallback(() => {
    Object.keys(pendingRequests.current)
      .filter(k => k.startsWith('alerts_'))
      .forEach(k => delete pendingRequests.current[k]);
    Object.keys(responseCache.current)
      .filter(k => k.startsWith('alerts_'))
      .forEach(k => delete responseCache.current[k]);
  }, []);

  const invalidateFeedback = useCallback(() => {
    Object.keys(pendingRequests.current)
      .filter(k => k.startsWith('feedback_'))
      .forEach(k => delete pendingRequests.current[k]);
    Object.keys(responseCache.current)
      .filter(k => k.startsWith('feedback_'))
      .forEach(k => delete responseCache.current[k]);
  }, []);

  const invalidateAll = useCallback(() => {
    pendingRequests.current = {};
    responseCache.current = {};
  }, []);

  // =========================================================================
  // WebSocket Cache Invalidation Subscriptions
  // =========================================================================

  /**
   * Subscribe to cache invalidation events from WebSocket
   * Maps backend cache key patterns to frontend data domains
   */
  useEffect(() => {
    // Map of cache key patterns to invalidation domains
    const subscriptions = [
      // User-related cache keys
      {
        pattern: 'admin:users:*',
        handler: () => {
          invalidateUsers();
          setInvalidationCounter(prev => ({ ...prev, users: prev.users + 1 }));
        },
      },
      // Employee-related cache keys
      {
        pattern: 'admin:employees:*',
        handler: () => {
          invalidateEmployees();
          setInvalidationCounter(prev => ({ ...prev, employees: prev.employees + 1 }));
        },
      },
      // Organization-related cache keys
      {
        pattern: 'admin:orgs:*',
        handler: () => {
          invalidateOrganizations();
          setInvalidationCounter(prev => ({ ...prev, organizations: prev.organizations + 1 }));
        },
      },
      // Analytics cache keys
      {
        pattern: 'admin:analytics:*',
        handler: () => {
          setInvalidationCounter(prev => ({ ...prev, analytics: prev.analytics + 1 }));
        },
      },
      // Economics cache keys
      {
        pattern: 'admin:economics:*',
        handler: () => {
          setInvalidationCounter(prev => ({ ...prev, economics: prev.economics + 1 }));
        },
      },
      // Feedback cache keys
      {
        pattern: 'admin:feedback:*',
        handler: () => {
          invalidateFeedback();
          setInvalidationCounter(prev => ({ ...prev, feedback: prev.feedback + 1 }));
        },
      },
      // Alerts cache keys
      {
        pattern: 'admin:alerts:*',
        handler: () => {
          invalidateAlerts();
          setInvalidationCounter(prev => ({ ...prev, alerts: prev.alerts + 1 }));
        },
      },
      // Tiers cache keys
      {
        pattern: 'admin:tiers:*',
        handler: () => {
          setInvalidationCounter(prev => ({ ...prev, tiers: prev.tiers + 1 }));
        },
      },
      // Monitoring cache keys
      {
        pattern: 'admin:monitoring:*',
        handler: () => {
          setInvalidationCounter(prev => ({ ...prev, monitoring: prev.monitoring + 1 }));
        },
      },
      // Knowledge Base cache keys
      {
        pattern: 'admin:kb:*',
        handler: () => {
          setInvalidationCounter(prev => ({ ...prev, kb: prev.kb + 1 }));
        },
      },
      // Full invalidation - invalidate everything
      {
        pattern: '*',
        handler: (event) => {
          if (event.event === 'full_invalidation') {
            console.log('[DataCache] Full cache invalidation received');
            invalidateAll();
            setInvalidationCounter(prev => ({
              users: prev.users + 1,
              employees: prev.employees + 1,
              organizations: prev.organizations + 1,
              alerts: prev.alerts + 1,
              feedback: prev.feedback + 1,
              analytics: prev.analytics + 1,
              economics: prev.economics + 1,
              tiers: prev.tiers + 1,
              monitoring: prev.monitoring + 1,
              kb: prev.kb + 1,
            }));
          }
        },
      },
    ];

    // Subscribe to all patterns
    const unsubscribes = subscriptions.map(({ pattern, handler }) =>
      subscribe(pattern, handler)
    );

    // Cleanup on unmount
    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [subscribe, invalidateUsers, invalidateEmployees, invalidateOrganizations, invalidateAlerts, invalidateFeedback, invalidateAll]);

  /**
   * Allow components to subscribe to specific domain invalidations
   * Returns a function to unsubscribe
   */
  const onInvalidate = useCallback((domain, callback) => {
    if (!invalidationCallbacks.current.has(domain)) {
      invalidationCallbacks.current.set(domain, new Set());
    }
    invalidationCallbacks.current.get(domain).add(callback);

    return () => {
      const callbacks = invalidationCallbacks.current.get(domain);
      if (callbacks) {
        callbacks.delete(callback);
      }
    };
  }, []);

  // =========================================================================
  // Data Fetchers - Backend Redis handles caching (1 hour TTL per ADR-080)
  // =========================================================================

  const getUsers = useCallback(async (params = { page: 1, limit: 20 }) => {
    const key = `users_${params.page}_${params.limit}_${params.search || ''}`;
    return fetchWithDedup(key, () => usersApi.list(params));
  }, [fetchWithDedup]);

  const getEmployees = useCallback(async (params = {}) => {
    const key = `employees_${params.status || 'all'}`;
    return fetchWithDedup(key, () => employeeApi.listAll(params));
  }, [fetchWithDedup]);

  const getPendingEmployees = useCallback(async () => {
    return fetchWithDedup('employees_pending', () => employeeApi.listPending());
  }, [fetchWithDedup]);

  const getOrganizationsTiles = useCallback(async (params = { page: 1, page_size: 20 }) => {
    const key = `orgs_tiles_${params.page}_${params.page_size}_${params.search || ''}_${params.plan_type || ''}`;
    return fetchWithDedup(key, () => organizationsApi.getTiles(params));
  }, [fetchWithDedup]);

  const getOrganizationsStats = useCallback(async () => {
    return fetchWithDedup('orgs_stats', () => organizationsApi.getStats());
  }, [fetchWithDedup]);

  const getOrganizationsHeatmap = useCallback(async () => {
    return fetchWithDedup('orgs_heatmap', () => organizationsApi.getHeatmap());
  }, [fetchWithDedup]);

  const getAlertsStats = useCallback(async () => {
    return fetchWithDedup('alerts_stats', () => alertsApi.getStats());
  }, [fetchWithDedup]);

  const getFeedbackStats = useCallback(async () => {
    return fetchWithDedup('feedback_stats', () => feedbackApi.getStats());
  }, [fetchWithDedup]);

  const getDeferredRevenue = useCallback(async () => {
    return fetchWithDedup('deferred_revenue', () => financeApi.getDeferredRevenue());
  }, [fetchWithDedup]);

  // =========================================================================
  // Credit Economics (ADR-079: Revenue Recognition)
  // =========================================================================

  const getTierCreditValues = useCallback(async () => {
    return fetchWithDedup('tier_credit_values', () => economicsApi.getTierCreditValues());
  }, [fetchWithDedup]);

  const getCreditEconomics = useCallback(async (params = {}) => {
    const key = `credit_economics_${params.organizationId || 'all'}_${params.month || 'current'}`;
    return fetchWithDedup(key, () => economicsApi.getCreditEconomics(params));
  }, [fetchWithDedup]);

  const getMarginPerCredit = useCallback(async (period = '30d') => {
    return fetchWithDedup(`margin_per_credit_${period}`, () => economicsApi.getMarginPerCredit(period));
  }, [fetchWithDedup]);

  const getProfitabilityByTier = useCallback(async (period = '30d') => {
    return fetchWithDedup(`profitability_by_tier_${period}`, () => economicsApi.getProfitabilityByTier(period));
  }, [fetchWithDedup]);

  const getMonthlyRevenueRecognition = useCallback(async (months = 6) => {
    return fetchWithDedup(`monthly_revenue_${months}`, () => economicsApi.getMonthlyRevenueRecognition(months));
  }, [fetchWithDedup]);

  // =========================================================================
  // Unit Economics (Phase 5: LLM Cost Tracking)
  // =========================================================================

  const getCostToServe = useCallback(async (period = '30d') => {
    return fetchWithDedup(`cost_to_serve_${period}`, () => economicsApi.getCostToServe(period));
  }, [fetchWithDedup]);

  // F4 (feature/cogs-per-credit-kpi): canonical $ COGS / credit headline + segments
  const getCogsPerCredit = useCallback(async (window = '30d') => {
    return fetchWithDedup(`cogs_per_credit_${window}`, () => economicsApi.getCogsPerCredit(window));
  }, [fetchWithDedup]);

  const getMonthlyPnL = useCallback(async (months = 6) => {
    return fetchWithDedup(`monthly_pnl_${months}`, () => economicsApi.getMonthlyPnL(months));
  }, [fetchWithDedup]);

  const getUnitEconomicsSummary = useCallback(async (period = '30d') => {
    return fetchWithDedup(`unit_economics_summary_${period}`, () => economicsApi.getUnitEconomicsSummary(period));
  }, [fetchWithDedup]);

  // =========================================================================
  // PM Analytics (Phase 3: ADR-081)
  // =========================================================================

  const getMRR = useCallback(async () => {
    return fetchWithDedup('analytics_mrr', () => analyticsApi.getMRR());
  }, [fetchWithDedup]);

  const getARR = useCallback(async () => {
    return fetchWithDedup('analytics_arr', () => analyticsApi.getARR());
  }, [fetchWithDedup]);

  const getMRRGrowth = useCallback(async (months = 6) => {
    return fetchWithDedup(`analytics_mrr_growth_${months}`, () => analyticsApi.getMRRGrowth(months));
  }, [fetchWithDedup]);

  const getRevenueByTier = useCallback(async () => {
    return fetchWithDedup('analytics_revenue_by_tier', () => analyticsApi.getRevenueByTier());
  }, [fetchWithDedup]);

  const getChurn = useCallback(async () => {
    return fetchWithDedup('analytics_churn', () => analyticsApi.getChurn());
  }, [fetchWithDedup]);

  const getAtRiskUsers = useCallback(async (riskThresholdDays = 14, limit = 20) => {
    return fetchWithDedup(`analytics_at_risk_${riskThresholdDays}_${limit}`, () => analyticsApi.getAtRiskUsers(riskThresholdDays, limit));
  }, [fetchWithDedup]);

  const getCohorts = useCallback(async (months = 6) => {
    return fetchWithDedup(`analytics_cohorts_${months}`, () => analyticsApi.getCohorts(months));
  }, [fetchWithDedup]);

  const getFeatureUsage = useCallback(async (period = '30d') => {
    return fetchWithDedup(`analytics_feature_usage_${period}`, () => analyticsApi.getFeatureUsage(period));
  }, [fetchWithDedup]);

  const getPowerUsers = useCallback(async (limit = 20, period = '30d') => {
    return fetchWithDedup(`analytics_power_users_${limit}_${period}`, () => analyticsApi.getPowerUsers(limit, period));
  }, [fetchWithDedup]);

  const getAnalyticsSummary = useCallback(async () => {
    return fetchWithDedup('analytics_summary', () => analyticsApi.getSummary());
  }, [fetchWithDedup]);

  // =========================================================================
  // Support Tools (Phase 6)
  // =========================================================================

  const supportUserLookup = useCallback(async (query, limit = 20) => {
    const key = `support_lookup_${query}_${limit}`;
    return fetchWithDedup(key, () => supportApi.userLookup(query, limit));
  }, [fetchWithDedup]);

  const getCustomerContext = useCallback(async (userId) => {
    return fetchWithDedup(`support_context_${userId}`, () => supportApi.getCustomerContext(userId));
  }, [fetchWithDedup]);

  const getUserActivitySummary = useCallback(async (userId) => {
    return fetchWithDedup(`support_activity_${userId}`, () => supportApi.getUserActivity(userId));
  }, [fetchWithDedup]);

  const getCreditUsageGraph = useCallback(async (userId, period = '30d') => {
    return fetchWithDedup(`support_credit_graph_${userId}_${period}`, () => supportApi.getCreditUsageGraph(userId, period));
  }, [fetchWithDedup]);

  const getSupportNotes = useCallback(async (userId) => {
    return fetchWithDedup(`support_notes_${userId}`, () => supportApi.getSupportNotes(userId));
  }, [fetchWithDedup]);

  // =========================================================================
  // Subscription Tiers (ADR-045: Config Page Instant Load)
  // =========================================================================

  const getTiers = useCallback(async () => {
    return fetchWithDedup('tiers_list', () => tiersApi.list());
  }, [fetchWithDedup]);

  const getTiersSyncStatus = useCallback(async () => {
    return fetchWithDedup('tiers_sync_status', () => tiersApi.getSyncStatus());
  }, [fetchWithDedup]);

  // =========================================================================
  // Monitoring (ADR-078: Cloud-First Monitoring)
  // =========================================================================

  const getMonitoringDashboards = useCallback(async () => {
    return fetchWithDedup('monitoring_dashboards', () => monitoringApi.listDashboards());
  }, [fetchWithDedup]);

  const getMonitoringHealth = useCallback(async () => {
    return fetchWithDedup('monitoring_health', () => healthApi.getCloudServicesHealth());
  }, [fetchWithDedup]);

  const getMonitoringAdminSummary = useCallback(async () => {
    return fetchWithDedup('monitoring_admin_summary', () => monitoringApi.getAdminSummary());
  }, [fetchWithDedup]);

  // =========================================================================
  // Knowledge Base (KB Management)
  // =========================================================================

  const getKBStats = useCallback(async () => {
    return fetchWithDedup('kb_stats', () => kbApi.getStats());
  }, [fetchWithDedup]);

  const getKBTopics = useCallback(async () => {
    return fetchWithDedup('kb_topics', () => kbApi.getTopics());
  }, [fetchWithDedup]);

  const getKBDomains = useCallback(async () => {
    return fetchWithDedup('kb_domains', () => kbApi.getGroupedStats("domain"));
  }, [fetchWithDedup]);

  // =========================================================================
  // Preloading - Warm up Redis cache on login
  // =========================================================================

  /**
   * Preload common data on login (NON-BLOCKING)
   *
   * Fires requests in background without awaiting - pages show skeleton
   * loaders until data arrives. This prevents login from blocking.
   *
   * Uses ref to ensure this only runs ONCE per session.
   */
  const preloadOnLogin = useCallback(() => {
    // Only run once per session
    if (hasPreloaded.current) {
      console.log('[DataCache] Already preloaded, skipping');
      return;
    }

    hasPreloaded.current = true;
    console.log('[DataCache] Preloading data (non-blocking)...');

    // Fire all requests in parallel - DON'T AWAIT (non-blocking)
    // Each request populates the frontend cache when complete
    // Pages show skeleton loaders until their data arrives
    getUsers({ page: 1, limit: 20 }).catch(e => console.warn('[DataCache] Users preload failed:', e));
    getEmployees({}).catch(e => console.warn('[DataCache] Employees preload failed:', e));
    getPendingEmployees().catch(e => console.warn('[DataCache] Pending employees preload failed:', e));
    getOrganizationsTiles({ page: 1, page_size: 20 }).catch(e => console.warn('[DataCache] Orgs preload failed:', e));
    getOrganizationsStats().catch(e => console.warn('[DataCache] Org stats preload failed:', e));
    getFeedbackStats().catch(e => console.warn('[DataCache] Feedback stats preload failed:', e));
    getDeferredRevenue().catch(e => console.warn('[DataCache] Deferred revenue preload failed:', e));

    // Economics endpoints for Dashboard instant load (ADR-079 fix)
    getMarginPerCredit('30d').catch(e => console.warn('[DataCache] Margin preload failed:', e));
    getProfitabilityByTier('30d').catch(e => console.warn('[DataCache] Profitability preload failed:', e));
    getCostToServe('30d').catch(e => console.warn('[DataCache] Cost to serve preload failed:', e));
    getMonthlyPnL(6).catch(e => console.warn('[DataCache] Monthly PnL preload failed:', e));

    // Subscription Tiers for Config page instant load
    getTiers().catch(e => console.warn('[DataCache] Tiers preload failed:', e));
    getTiersSyncStatus().catch(e => console.warn('[DataCache] Tiers sync status preload failed:', e));

    // Monitoring for Monitoring page instant load
    getMonitoringDashboards().catch(e => console.warn('[DataCache] Monitoring dashboards preload failed:', e));
    getMonitoringHealth().catch(e => console.warn('[DataCache] Monitoring health preload failed:', e));
    getMonitoringAdminSummary().catch(e => console.warn('[DataCache] Monitoring summary preload failed:', e));

    // Knowledge Base for KB page instant load
    getKBStats().catch(e => console.warn('[DataCache] KB stats preload failed:', e));
    getKBTopics().catch(e => console.warn('[DataCache] KB topics preload failed:', e));
    getKBDomains().catch(e => console.warn('[DataCache] KB domains preload failed:', e));

    // Analytics for Analytics page instant load (PM Dashboard)
    // Keys: analytics_mrr, analytics_arr, analytics_churn, analytics_cohorts_6,
    //       analytics_feature_usage_30d, analytics_power_users_20_30d, analytics_summary
    getMRR().catch(e => console.warn('[DataCache] MRR preload failed:', e));
    getARR().catch(e => console.warn('[DataCache] ARR preload failed:', e));
    getChurn().catch(e => console.warn('[DataCache] Churn preload failed:', e));
    getCohorts(6).catch(e => console.warn('[DataCache] Cohorts preload failed:', e));
    getFeatureUsage('30d').catch(e => console.warn('[DataCache] Feature usage preload failed:', e));
    getPowerUsers(20, '30d').catch(e => console.warn('[DataCache] Power users preload failed:', e));
    getAnalyticsSummary().catch(e => console.warn('[DataCache] Analytics summary preload failed:', e));
    getRevenueByTier().catch(e => console.warn('[DataCache] Revenue by tier preload failed:', e));
    getAtRiskUsers(14, 20).catch(e => console.warn('[DataCache] At-risk users preload failed:', e));
    getMRRGrowth(6).catch(e => console.warn('[DataCache] MRR growth preload failed:', e));

    console.log('[DataCache] Preload requests fired (full: economics, tiers, monitoring, kb, analytics)');
  }, [getUsers, getEmployees, getPendingEmployees, getOrganizationsTiles, getOrganizationsStats, getFeedbackStats, getDeferredRevenue, getMarginPerCredit, getProfitabilityByTier, getCostToServe, getMonthlyPnL, getTiers, getTiersSyncStatus, getMonitoringDashboards, getMonitoringHealth, getMonitoringAdminSummary, getKBStats, getKBTopics, getKBDomains, getMRR, getARR, getChurn, getCohorts, getFeatureUsage, getPowerUsers, getAnalyticsSummary, getRevenueByTier, getAtRiskUsers, getMRRGrowth]);

  /**
   * Clear session state (on logout)
   */
  const clearCache = useCallback(() => {
    pendingRequests.current = {};
    responseCache.current = {};
    hasPreloaded.current = false;
  }, []);

  const value = {
    // Synchronous cache access (for instant rendering with cached data)
    getCachedValue,

    // Data fetchers
    getUsers,
    getEmployees,
    getPendingEmployees,
    getOrganizationsTiles,
    getOrganizationsStats,
    getOrganizationsHeatmap,
    getFeedbackStats,
    getDeferredRevenue,

    // Credit Economics (ADR-079)
    getTierCreditValues,
    getCreditEconomics,
    getMarginPerCredit,
    getProfitabilityByTier,
    getMonthlyRevenueRecognition,

    // Unit Economics (Phase 5)
    getCostToServe,
    getMonthlyPnL,
    getUnitEconomicsSummary,
    getCogsPerCredit,

    // PM Analytics (Phase 3: ADR-081)
    getMRR,
    getARR,
    getMRRGrowth,
    getRevenueByTier,
    getChurn,
    getAtRiskUsers,
    getCohorts,
    getFeatureUsage,
    getPowerUsers,
    getAnalyticsSummary,

    // Support Tools (Phase 6)
    supportUserLookup,
    getCustomerContext,
    getUserActivitySummary,
    getCreditUsageGraph,
    getSupportNotes,

    // Subscription Tiers (Config page)
    getTiers,
    getTiersSyncStatus,

    // Monitoring (Cloud-first)
    getMonitoringDashboards,
    getMonitoringHealth,
    getMonitoringAdminSummary,

    // Knowledge Base
    getKBStats,
    getKBTopics,
    getKBDomains,

    // Lifecycle
    preloadOnLogin,
    clearCache,

    // Invalidation helpers
    invalidateUsers,
    invalidateEmployees,
    invalidateOrganizations,
    invalidateAlerts,
    invalidateFeedback,
    invalidateAll,

    // WebSocket cache events
    isWebSocketConnected: isConnected,
    lastCacheEvent: lastEvent,
    invalidationCounter,
    onInvalidate,
  };

  return (
    <DataCacheContext.Provider value={value}>
      {children}
    </DataCacheContext.Provider>
  );
};
